import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Send, Loader2, MessageCircle, Bot, User, RefreshCw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Componente chat messaggi per il paziente.
 * - Cronologia persistita per 60 giorni
 * - I messaggi più vecchi di 60 giorni vengono eliminati automaticamente
 */
const PatientMessagesTab = ({ userId }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    loadMessages();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadMessages = async () => {
    try {
      const res = await axios.get(`${API}/api/patient/messages/history`, { headers });
      setMessages(res.data.messages || []);
    } catch (error) {
      console.error('Error loading messages:', error);
      // Mostra un messaggio di benvenuto se non ci sono messaggi
      setMessages([{
        id: 'welcome',
        role: 'assistant',
        content: 'Ciao! Sono l\'assistente virtuale di PRIME VISION. Come posso aiutarti oggi? 👁️\n\nPuoi chiedermi informazioni su:\n• Prenotazioni e appuntamenti\n• Preparazione alle visite\n• Informazioni sui nostri servizi\n• Domande generali sulla salute degli occhi',
        created_at: new Date().toISOString()
      }]);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    const messageText = newMessage.trim();
    setNewMessage('');
    setSending(true);

    // Aggiungi messaggio utente ottimisticamente
    const tempUserMsg = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content: messageText,
      created_at: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempUserMsg]);

    try {
      const res = await axios.post(
        `${API}/api/patient/messages/send`,
        { message: messageText },
        { headers, timeout: 60000 }
      );

      // Sostituisci messaggio temporaneo con quello reale e aggiungi risposta
      setMessages(prev => {
        const withoutTemp = prev.filter(m => m.id !== tempUserMsg.id);
        return [...withoutTemp, res.data.user_message, res.data.assistant_message];
      });
    } catch (error) {
      toast.error('Errore nell\'invio del messaggio');
      // Rimuovi messaggio temporaneo
      setMessages(prev => prev.filter(m => m.id !== tempUserMsg.id));
      setNewMessage(messageText); // Ripristina testo
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return `Oggi ${date.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`;
    } else if (date.toDateString() === yesterday.toDateString()) {
      return `Ieri ${date.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`;
    } else {
      return date.toLocaleDateString('it-IT', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#2A9FCB' }} />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[600px] bg-white rounded-2xl shadow-lg overflow-hidden" data-testid="patient-messages-tab">
      {/* Header */}
      <div className="p-4 border-b flex items-center justify-between" style={{ background: 'linear-gradient(135deg, #2A9FCB 0%, #1a6b8a 100%)' }}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
            <Bot className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-white">Assistente PRIME VISION</h3>
            <p className="text-white/70 text-xs">Rispondiamo in pochi secondi</p>
          </div>
        </div>
        <button
          onClick={loadMessages}
          className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          title="Aggiorna"
        >
          <RefreshCw className="w-5 h-5 text-white" />
        </button>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
        {messages.length === 0 ? (
          <div className="text-center text-gray-400 py-12">
            <MessageCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Nessun messaggio</p>
            <p className="text-sm">Scrivi un messaggio per iniziare la conversazione</p>
          </div>
        ) : (
          messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] rounded-2xl p-4 ${
                  msg.role === 'user'
                    ? 'bg-gradient-to-r from-[#2A9FCB] to-[#1a6b8a] text-white rounded-br-md'
                    : 'bg-white shadow-sm border border-gray-100 rounded-bl-md'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  {msg.role === 'user' ? (
                    <User className="w-4 h-4" />
                  ) : (
                    <Bot className="w-4 h-4" style={{ color: '#2A9FCB' }} />
                  )}
                  <span className={`text-xs ${msg.role === 'user' ? 'text-white/70' : 'text-gray-400'}`}>
                    {msg.role === 'user' ? 'Tu' : 'Assistente'}
                  </span>
                </div>
                <p className={`text-sm whitespace-pre-wrap ${msg.role === 'user' ? 'text-white' : 'text-gray-700'}`}>
                  {msg.content}
                </p>
                <p className={`text-xs mt-2 ${msg.role === 'user' ? 'text-white/50' : 'text-gray-300'}`}>
                  {formatTime(msg.created_at)}
                </p>
              </div>
            </div>
          ))
        )}
        
        {/* Typing indicator when sending */}
        {sending && (
          <div className="flex justify-start">
            <div className="bg-white shadow-sm border border-gray-100 rounded-2xl rounded-bl-md p-4">
              <div className="flex items-center gap-2">
                <Bot className="w-4 h-4" style={{ color: '#2A9FCB' }} />
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <form onSubmit={sendMessage} className="p-4 border-t bg-white">
        <div className="flex gap-2">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Scrivi un messaggio..."
            className="flex-1 p-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[#2A9FCB] focus:border-transparent"
            disabled={sending}
            data-testid="message-input"
          />
          <button
            type="submit"
            disabled={!newMessage.trim() || sending}
            className="p-3 rounded-xl text-white transition-all hover:opacity-90 disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg, #2A9FCB 0%, #1a6b8a 100%)' }}
            data-testid="send-message-btn"
          >
            {sending ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </div>
        <p className="text-xs text-gray-400 mt-2 text-center">
          📝 I messaggi vengono conservati per 60 giorni
        </p>
      </form>
    </div>
  );
};

export default PatientMessagesTab;
